import styled from 'styled-components';
import { isCAMSDefaultHost } from '../../../../utils/cams';

// Helper to get theme value or default based on hostname
const getThemeValue = (theme, key, defaultValue) => {
  if (isCAMSDefaultHost()) return defaultValue;
  return theme?.brand?.[key] || defaultValue;
};

// Full page wrapper with geometric background
export const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  ${({ theme }) => {
    if (isCAMSDefaultHost()) {
      return 'background: #0D256D;';
    }
    return theme?.brand?.backgroundImage
      ? `background: url(${theme.brand.backgroundImage}), ${theme?.brand?.background || '#0D256D'};
         background-size: cover;`
      : `background: ${theme?.brand?.background || '#0D256D'};`;
  }}
  position: relative;
  overflow: hidden;
`;

// Large blob shapes
export const BlobShape = styled.div`
  position: absolute;
  pointer-events: none;

  &.green-blob {
    background-color: #6EE7B7;
    width: 320px;
    height: 320px;
    clip-path: ellipse(65% 50% at 20% 40%);
    top: -64px;
    left: -128px;
    opacity: 0.7;
  }

  &.blue-blob {
    background-color: #5C6BC0;
    width: 320px;
    height: 320px;
    clip-path: ellipse(70% 50% at 80% 30%);
    top: -64px;
    right: -128px;
    opacity: 0.8;
  }
`;

// Geometric shapes container
export const GeometricShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

// Individual shape styles
export const Shape = styled.div`
  position: absolute;

  &.circle {
    border-radius: 50%;
    background-color: ${({ color }) => color || '#6EE7B7'};
    opacity: ${({ opacity }) => opacity || 0.7};
  }

  &.hollow-square {
    border-radius: 6px;
    border: 2px solid ${({ color }) => color || '#6EE7B7'};
    background-color: transparent;
    opacity: ${({ opacity }) => opacity || 0.8};
  }

  &.solid-square {
    border-radius: 6px;
    background-color: ${({ color }) => color || '#5C6BC0'};
    opacity: ${({ opacity }) => opacity || 0.7};
  }
`;

// Header for CAMS page
export const CamsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  position: relative;
  z-index: 10;
  ${({ theme }) => {
    if (isCAMSDefaultHost()) {
      return 'background: rgba(0, 0, 0, 0.4);';
    }
    return theme?.brand?.headerBackgroundImage
      ? `background: url(${theme.brand.headerBackgroundImage}), ${theme?.brand?.headerBackground || 'rgba(0, 0, 0, 0.4)'};
         background-size: cover;`
      : `background: ${theme?.brand?.headerBackground || 'rgba(0, 0, 0, 0.4)'};`;
  }}
`;

export const LogoContainer = styled.div`
  img {
    /* Both axes are capped so an SVG without an intrinsic size still lands inside the
       header, and object-fit keeps the aspect ratio when a cap applies. */
    max-height: 50px;
    max-width: 120px;
    width: auto;
    height: 40px;
    object-fit: contain;
  }
`;

export const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 25px;
`;

export const HeaderLink = styled.a`
  color: ${({ theme }) => theme?.brand?.headerText || '#fff'};
  text-decoration: none;
  font-size: 0.95em;
  font-weight: 400;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
    color: ${({ theme }) => theme?.brand?.headerText || '#fff'};
  }
`;

export const LanguageToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme?.brand?.headerText || '#fff'};
  font-size: 0.95em;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

// Main content area
export const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 40px;
  position: relative;
  z-index: 5;
`;

// Login card wrapper
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 60px 50px;
  background-color: #fff;
  border-radius: 3px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 576px) {
    padding: 30px 30px 40px;
    margin: 0 15px;
  }
`;

export const Title = styled.h1`
  margin-bottom: 15px;
  margin-top: 0;
  font-family: 'Muli', sans-serif;
  font-weight: 600;
  font-size: 29px;
  line-height: 1.2;
  color: ${({ theme }) => getThemeValue(theme, 'title', '#331850')};
  text-align: center;
`;

export const Subtitle = styled.p`
  margin-bottom: 25px;
  margin-top: 0;
  padding-bottom: 15px;
  font-weight: 400;
  font-size: 0.95em;
  line-height: 1.5;
  color: ${({ theme }) => getThemeValue(theme, 'text', '#495c6d')};
  text-align: center;
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const InputLabel = styled.label`
  margin-bottom: 6px;
  display: block;
  font-weight: 300;
  font-size: 85%;
  line-height: 1.2;
  color: #607486;
  text-transform: uppercase;
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #b7c4d5;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1em;
  color: #5d6f84;
  outline: none;
  transition: border-color ease-in-out 0.15s;
  background-color: #fff;
  min-height: 34px;

  &:focus {
    border-color: ${({ theme }) => theme?.brand?.primary || '#4a90d9'};
  }

  &::placeholder {
    color: #999;
  }

  &.is-invalid {
    border-color: red;
  }
`;

export const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
`;

export const InputRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-start;

  & > div:first-child {
    flex: 1;
    min-width: 0;
  }

  & > div:last-child {
    flex: 1.5;
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;

    & > div:first-child,
    & > div:last-child {
      flex: 1;
      width: 100%;
    }
  }
`;

export const InputIconWrapper = styled.div`
  position: relative;
`;

export const InputIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b7c4d5;
  display: flex;
  align-items: center;
`;

export const TooltipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
`;

export const InputWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${Input} {
    flex: 1;
  }
`;

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

export const HelpIconButton = styled.button`
  width: 22px;
  height: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid #607486;
  color: #607486;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    border-color: #331850;
    color: #331850;
  }

  &:focus {
    outline: none;
  }
`;

export const TooltipBubble = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background-color: ${({ theme }) => getThemeValue(theme, 'primary', '#5CB85C')};
  color: ${({ theme }) => getThemeValue(theme, 'primaryText', '#fff')};
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 400;
  line-height: 1.4;
  width: 250px;
  text-align: left;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${({ theme }) => getThemeValue(theme, 'primary', '#5CB85C')};
  }

  ${TooltipContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

export const HelpIcon = styled.a`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #000;
  color: #000;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    text-decoration: none;
    color: #000;
  }
`;

export const Button = styled.button`
  background-color: ${({ theme }) => getThemeValue(theme, 'primary', '#5CB85C')};
  border: none;
  border-color: ${({ theme }) => getThemeValue(theme, 'primary', '#5CB85C')};
  color: ${({ theme }) => getThemeValue(theme, 'primaryText', '#fff')};
  padding: 12px 32px;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  width: 100%;
  transition: opacity 0.2s ease;
  margin-top: 5px;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-loading {
    svg {
      animation: spin 1s linear infinite;
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    }
  }
`;

export const ErrorMessage = styled.div`
  margin-top: 15px;
  font-weight: 400;
  font-size: 0.9em;
  line-height: 1.3;
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
  padding: 12px 15px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0;
  animation: fadeIn 0.3s ease-in forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const SuccessMessage = styled.div`
  margin-top: 15px;
  font-weight: 400;
  font-size: 0.9em;
  line-height: 1.3;
  color: #155724;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  padding: 12px 15px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0;
  animation: fadeIn 0.3s ease-in forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const FieldErrorMessage = styled.span`
  font-size: 0.85em;
  color: red;
  display: block;
  margin-top: 5px;
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
`;

export const LoginMeta = styled.p`
  text-align: center;
  width: 250px;
  margin: 20px auto 0;
  color: ${({ theme }) => getThemeValue(theme, 'footerText', '#e9eff4')};
  font-size: 0.9em;
  position: relative;
  z-index: 5;
`;

export const LoginLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 20px;
  color: #000;
  font-size: 0.9em;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #333;
    text-decoration: underline;
  }
`;

export const AccessCodeLink = styled.a`
  display: inline-block;
  margin-top: 6px;
  color: #607486;
  font-size: 0.7em;
  white-space: nowrap;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #331850;
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    white-space: normal;
  }
`;

// Footer styles
export const CamsFooter = styled.footer`
  margin-top: 20px;
  text-align: center;
  position: relative;
  z-index: 5;
  width: 100%;
  max-width: 460px;
`;

export const FooterLinksGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  margin-bottom: 10px;

  a {
    color: ${({ theme }) => getThemeValue(theme, 'footerText', '#e9eff4')};
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
  }

  span {
    color: ${({ theme }) => getThemeValue(theme, 'footerText', '#e9eff4')};
  }
`;

export const FooterCopyright = styled.div`
  font-size: 12px;
  color: ${({ theme }) => getThemeValue(theme, 'footerText', '#e9eff4')};
`;
